import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Upload, CheckCircle, Loader2, X, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import type { VoterImportRemapStartedResponse } from '@signalvote/shared';
import { API_BASE_URL } from '../../config';
import { ApiErrorHandler } from '../../utils/apiErrorHandler';
import { extractCsvPreview } from '../../utils/csvPreview';
import { useRequestUpload, useImportFromSpaces, useTags, useStagingTableInfo } from '../../hooks/queries';
import { useFileUpload } from '../../hooks/useFileUpload';
import type { UploadState } from '../../hooks/useFileUpload';
import { autoDetectColumns as detectColumns, isMappingValid } from './voterImportUtils';

interface MappingState {
  columns: string[];
  sampleData: Record<string, string>[];
  columnMapping: Record<string, string>;
  selectedTags: number[];
  delimiter: string;
  fileKey: string | null;
  fileName: string | null;
}

const INITIAL_MAPPING: MappingState = {
  columns: [],
  sampleData: [],
  columnMapping: {},
  selectedTags: [],
  delimiter: ',',
  fileKey: null,
  fileName: null,
};

export interface VoterStagingImportProps {
  // Parent-controlled state (from VoterDataImportCard). When omitted, component manages its own.
  uploadState?: UploadState & {
    file: File | null;
    upload: (file: File, uploadUrl: string, fileKey: string) => Promise<void>;
    abort: () => void;
    reset: () => void;
  };
  mappingState?: MappingState;
  onMappingChange?: (state: MappingState | ((prev: MappingState) => MappingState)) => void;
  onImportStarted?: () => void;
  onReset?: () => void;
  // Remap flow — fetch columns from existing staging table
  existingStagingTable?: string;
  // Legacy callback (maps to onImportStarted)
  onComplete?: () => void;
}

interface ColumnMapping {
  voterIdColumn?: string;
  firstNameColumn?: string;
  middleNameColumn?: string;
  lastNameColumn?: string;
  addressColumn?: string;
  cityColumn?: string;
  stateColumn?: string;
  zipColumn?: string;
  ageColumn?: string;
  genderColumn?: string;
  phoneColumn?: string;
  emailColumn?: string;
  partyColumn?: string;
  voteFrequencyColumn?: string;
  ethnicityColumn?: string;
  religionColumn?: string;
  incomeColumn?: string;
  latitudeColumn?: string;
  longitudeColumn?: string;
  voterSupportColumn?: string;
  lastContactStatusColumn?: string;
  smsConsentStatusColumn?: string;
  geocodingAccuracyColumn?: string;
  voteFrequencyRawColumn?: string;
  educationColumn?: string;
  conservativeDonorColumn?: string;
  liberalDonorColumn?: string;
  tagIds?: number[];
}

const NONE_SENTINEL = '__none__';

const VoterStagingImport: React.FC<VoterStagingImportProps> = ({
  uploadState: externalUpload,
  mappingState: externalMapping,
  onMappingChange: externalOnMappingChange,
  onImportStarted,
  onReset,
  existingStagingTable,
  onComplete,
}) => {
  // Internal state fallbacks for standalone/remap callers
  const internalUploadHook = useFileUpload();
  const [internalMapping, setInternalMapping] = useState<MappingState>(INITIAL_MAPPING);

  // Use parent-controlled state when provided, otherwise internal
  const uploadState = externalUpload ?? internalUploadHook;
  const mappingState = externalMapping ?? internalMapping;
  const onMappingChange = externalOnMappingChange ?? setInternalMapping;
  const handleImportStarted = onImportStarted ?? onComplete;

  const [activeStep, setActiveStep] = useState(
    existingStagingTable ? 1 : mappingState.columns.length > 0 ? 1 : 0
  );
  const [error, setError] = useState<string | null>(null);
  const [tagPopoverOpen, setTagPopoverOpen] = useState(false);

  const tagsRead = useTags(undefined);
  const availableTags = tagsRead.status === 'ready' ? tagsRead.data : [];
  const { mutateAsync: requestUpload, isPending: requestUploadPending } = useRequestUpload();
  const importFromSpaces = useImportFromSpaces();

  const steps = ['Select File', 'Map Columns', 'Import'];

  const SMALL_FILE_THRESHOLD = 5 * 1024 * 1024; // 5MB
  const isLargeFile = (uploadState.file?.size ?? 0) > SMALL_FILE_THRESHOLD;

  const handleFileSelect = useCallback(async (file: File) => {
    if (!file.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    setError(null);

    try {
      // 1. Parse headers client-side (instant, ~100KB read)
      const preview = await extractCsvPreview(file);

      // 2. Request presigned upload URL from backend
      const { uploadUrl, fileKey, usesPresignedUrls } = await requestUpload(file.name);

      // 3. Start upload in background (via parent's hook)
      uploadState.upload(file, uploadUrl, fileKey, usesPresignedUrls).catch(() => {
        // Error state is handled by the hook — no need to re-throw
      });

      // 4. Store preview + mapping info in parent state
      onMappingChange((prev) => ({
        ...prev,
        columns: preview.headers,
        sampleData: preview.sampleRows,
        delimiter: preview.delimiter,
        fileKey,
        fileName: file.name,
      }));

      // 5. Auto-detect column mappings
      autoDetectColumns(preview.headers);

      // 6. Show encoding warning if needed
      if (preview.encodingWarning) {
        setError(preview.encodingWarning);
      }

      // 7. Advance to mapping step
      setActiveStep(1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process file');
    }
  }, [requestUpload, uploadState, onMappingChange]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  const handleChangeFile = useCallback(() => {
    uploadState.abort();
    onReset?.();
    setActiveStep(0);
    setError(null);
  }, [uploadState, onReset]);

  const autoDetectColumns = useCallback((columns: string[]) => {
    const mapping = detectColumns(columns);
    onMappingChange((prev) => ({ ...prev, columnMapping: mapping }));
  }, [onMappingChange]);

  // Remap flow: seed the mapping from the staging table already on the server.
  // React Query owns the request and is keyed on the table name, so a response
  // for a table the user has moved off can no longer overwrite the current one.
  const { data: stagingInfo, error: stagingError } = useStagingTableInfo(existingStagingTable);

  // Seed the mapping the FIRST time a given staging table's columns arrive. A
  // staging table is an uploaded snapshot, so its columns never change under
  // us; re-running auto-detect on a later refetch would only overwrite the
  // column choices the user has made since.
  const seededTableRef = useRef<string | null>(null);
  useEffect(() => {
    if (!stagingInfo || !existingStagingTable) return;
    if (seededTableRef.current === existingStagingTable) return;
    seededTableRef.current = existingStagingTable;
    onMappingChange((prev) => ({
      ...prev,
      columns: stagingInfo.columns,
      sampleData: stagingInfo.sampleData.map((row) =>
        Object.fromEntries(Object.entries(row).map(([col, value]) => [col, value == null ? '' : String(value)])),
      ),
    }));
    autoDetectColumns(stagingInfo.columns);
  }, [stagingInfo, existingStagingTable, onMappingChange, autoDetectColumns]);

  // One banner for both, and dismissable either way. Remembering WHICH table's
  // failure was dismissed (rather than a bare flag) means pointing the remap at
  // a different table surfaces its own failure again, with no Effect to reset.
  const [dismissedErrorFor, setDismissedErrorFor] = useState<string | null>(null);
  const stagingErrorMessage =
    stagingError instanceof Error && dismissedErrorFor !== existingStagingTable
      ? stagingError.message
      : null;
  const bannerError = error ?? stagingErrorMessage;

  const handleImport = async () => {
    const { columnMapping, selectedTags, delimiter, fileKey, fileName } = mappingState;

    if (!columnMapping.firstNameColumn || !columnMapping.lastNameColumn || !columnMapping.addressColumn) {
      setError('Please map at least First Name, Last Name, and Address columns');
      return;
    }

    const targetFileKey = fileKey || (existingStagingTable ? null : null);
    if (!targetFileKey && !existingStagingTable) {
      setError('No file uploaded');
      return;
    }

    setError(null);

    try {
      if (existingStagingTable) {
        // Remap flow — use existing staging table
        const result: VoterImportRemapStartedResponse = await ApiErrorHandler.makeAuthenticatedRequest(
          `${API_BASE_URL}/api/voterimport/remap`,
          {
            method: 'POST',
            body: JSON.stringify({
              stagingTableName: existingStagingTable,
              mapping: { ...columnMapping, tagIds: selectedTags },
            }),
          }
        );
        if (result.jobId) {
          handleImportStarted?.();
        }
      } else {
        // New Spaces-based import
        await importFromSpaces.mutateAsync({
          fileKey: targetFileKey!,
          fileName: fileName ?? 'import.csv',
          delimiter,
          mapping: { ...columnMapping, tagIds: selectedTags },
        });
        handleImportStarted?.();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start import');
    }
  };

  const { columnMapping, selectedTags } = mappingState;

  const mappingValid = isMappingValid(columnMapping);
  const canImport = existingStagingTable
    ? mappingValid
    : mappingValid && uploadState.complete;

  const handleTagToggle = (tagId: number) => {
    onMappingChange((prev) => {
      const current = prev.selectedTags;
      const next = current.includes(tagId)
        ? current.filter((id) => id !== tagId)
        : [...current, tagId];
      return { ...prev, selectedTags: next };
    });
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold mb-1">Select CSV File</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Select a CSV file containing voter information. Headers will be parsed instantly
                for column mapping while the file uploads in the background.
              </p>

              <div
                className="mt-3 p-8 border-2 border-dashed border-muted-foreground/30 rounded-md bg-muted/50 text-center cursor-pointer hover:border-primary hover:bg-muted transition-colors"
                onDragOver={(e: React.DragEvent) => e.preventDefault()}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-12 w-12 text-primary mx-auto mb-2" />
                <p className="text-base mb-1">
                  Drag and drop a CSV file here
                </p>
                <p className="text-sm text-muted-foreground mb-3">
                  or
                </p>
                <Button
                  disabled={requestUploadPending}
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                >
                  {requestUploadPending ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    'Choose File'
                  )}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".csv"
                  onChange={handleInputChange}
                />
              </div>
            </CardContent>
          </Card>
        );

      case 1:
        return (
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold mb-2">Map CSV Columns to Voter Fields</h3>

              {/* Upload progress bar — only shown for large files (>5MB) */}
              {!existingStagingTable && uploadState.uploading && isLargeFile && (
                <div className="mb-4">
                  <div className="flex justify-between mb-1">
                    <span className="text-xs text-muted-foreground">
                      Uploading {uploadState.fileName}...
                    </span>
                    <span className="text-xs text-muted-foreground">{uploadState.progress}%</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: `${uploadState.progress}%` }}
                    />
                  </div>
                  {uploadState.stalled && (
                    <p className="text-xs text-yellow-600 mt-1">
                      Upload appears stalled — check your network connection
                    </p>
                  )}
                </div>
              )}

              {uploadState.error && (
                <div className="mb-4 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                  {uploadState.error}
                </div>
              )}

              {uploadState.complete && !existingStagingTable && isLargeFile && (
                <div className="mb-4 rounded-md border border-green-500/50 bg-green-50 p-3 text-sm text-green-700 dark:bg-green-950/20 dark:text-green-400">
                  File uploaded successfully
                </div>
              )}

              <div className="flex justify-between items-start mb-4">
                <p className="text-sm text-muted-foreground">
                  {mappingState.columns.length} columns detected.
                  Map your CSV columns to the corresponding voter fields. Required fields are marked with *.
                </p>
                {!existingStagingTable && (
                  <Button size="sm" variant="outline" onClick={handleChangeFile} className="ml-4 shrink-0">
                    Change File
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[50vh] overflow-y-auto pr-1">
                {[
                  { field: 'firstNameColumn', label: 'First Name *', required: true },
                  { field: 'lastNameColumn', label: 'Last Name *', required: true },
                  { field: 'addressColumn', label: 'Address *', required: true },
                  { field: 'cityColumn', label: 'City' },
                  { field: 'stateColumn', label: 'State' },
                  { field: 'zipColumn', label: 'ZIP Code' },
                  { field: 'voterIdColumn', label: 'Voter ID' },
                  { field: 'middleNameColumn', label: 'Middle Name' },
                  { field: 'ageColumn', label: 'Age' },
                  { field: 'genderColumn', label: 'Gender' },
                  { field: 'phoneColumn', label: 'Phone' },
                  { field: 'emailColumn', label: 'Email' },
                  { field: 'partyColumn', label: 'Party Affiliation' },
                  { field: 'voteFrequencyColumn', label: 'Vote Frequency' },
                  { field: 'ethnicityColumn', label: 'Ethnicity' },
                  { field: 'religionColumn', label: 'Religion' },
                  { field: 'incomeColumn', label: 'Income' },
                  { field: 'latitudeColumn', label: 'Latitude' },
                  { field: 'longitudeColumn', label: 'Longitude' },
                  { field: 'voterSupportColumn', label: 'Voter Support' },
                  { field: 'lastContactStatusColumn', label: 'Last Contact Status' },
                  { field: 'geocodingAccuracyColumn', label: 'Geocoding Accuracy' },
                  { field: 'smsConsentStatusColumn', label: 'SMS Consent Status' },
                  { field: 'voteFrequencyRawColumn', label: 'Vote Frequency (Numeric)' },
                  { field: 'educationColumn', label: 'Education' },
                  { field: 'conservativeDonorColumn', label: 'Conservative Donor' },
                  { field: 'liberalDonorColumn', label: 'Liberal Donor' },
                ].map(({ field, label }) => (
                  <div key={field} className="space-y-1">
                    <label className="text-sm font-medium text-foreground">{label}</label>
                    <Select
                      value={columnMapping[field] || NONE_SENTINEL}
                      onValueChange={(value) => {
                        const mapped = value === NONE_SENTINEL ? '' : value;
                        onMappingChange((prev) => ({
                          ...prev,
                          columnMapping: { ...prev.columnMapping, [field]: mapped },
                        }));
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="-- Not Mapped --" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60 overflow-y-auto">
                        <SelectItem value={NONE_SENTINEL}>
                          <em>-- Not Mapped --</em>
                        </SelectItem>
                        {mappingState.columns.map(col => (
                          <SelectItem key={col} value={col}>{col}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>

              {/* Tag Selection */}
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-2">Apply Tags (Optional)</h3>
                <Popover open={tagPopoverOpen} onOpenChange={setTagPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-between font-normal"
                    >
                      {selectedTags.length > 0 ? (
                        <div className="flex flex-wrap gap-1 overflow-hidden">
                          {selectedTags.map((value) => {
                            const tag = availableTags.find((t) => t.id === value);
                            return (
                              <Badge
                                key={value}
                                variant="secondary"
                                className="text-xs"
                                style={{ backgroundColor: tag?.color ?? undefined }}
                              >
                                {tag?.tagName || value}
                              </Badge>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">Select Tags</span>
                      )}
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-2" align="start">
                    {availableTags.map((tag) => (
                      <div
                        key={tag.id}
                        className="flex items-center gap-2 rounded-sm px-2 py-1.5 cursor-pointer hover:bg-accent"
                        onClick={() => handleTagToggle(tag.id)}
                      >
                        <Checkbox
                          checked={selectedTags.includes(tag.id)}
                          onCheckedChange={() => handleTagToggle(tag.id)}
                        />
                        <span className="flex-1 text-sm">{tag.tagName}</span>
                        <span
                          className="inline-block h-4 w-4 rounded-full shrink-0"
                          style={{ backgroundColor: tag.color ?? undefined }}
                        />
                      </div>
                    ))}
                    {tagsRead.status === 'failed' || tagsRead.status === 'offline' ? (
                      <p role="alert" className="text-sm text-muted-foreground text-center py-2">
                        {tagsRead.status === 'offline' ? "You're offline, so tags can't load." : "Couldn't load tags."}
                      </p>
                    ) : tagsRead.status === 'ready' && availableTags.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-2">No tags available</p>
                    ) : null}
                  </PopoverContent>
                </Popover>
              </div>

              {/* Import button */}
              <Button
                onClick={handleImport}
                disabled={!canImport || importFromSpaces.isPending}
                className="mt-6 w-full"
              >
                {importFromSpaces.isPending ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : uploadState.uploading ? (
                  `Uploading... ${uploadState.progress}%`
                ) : !mappingValid ? (
                  'Map required fields'
                ) : (
                  'Import Voters'
                )}
              </Button>

              {/* Sample data preview */}
              {mappingState.sampleData.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-semibold mb-2">
                    Sample Data Preview
                    <span className="ml-2 font-normal text-muted-foreground">
                      ({mappingState.columns.length} columns)
                    </span>
                  </h4>
                  <div className="rounded-md border max-h-[300px] overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {mappingState.columns.map(col => (
                            <TableHead key={col} className="whitespace-nowrap">{col}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {mappingState.sampleData.map((row, idx) => (
                          <TableRow key={idx}>
                            {mappingState.columns.map(col => (
                              <TableCell key={col} className="whitespace-nowrap">{row[col] || '-'}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );

      case 2:
        return (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-6">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
                <h3 className="text-lg font-semibold mt-4 mb-1">
                  Import Started!
                </h3>
                <p className="text-sm text-muted-foreground">
                  Your import is running in the background. You can close this dialog
                  and monitor progress from the import card.
                </p>
              </div>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      {/* Custom Stepper */}
      <div className="flex items-center mb-8">
        {steps.map((label, index) => (
          <React.Fragment key={label}>
            {index > 0 && (
              <div
                className={cn(
                  'flex-1 h-0.5 mx-2',
                  index <= activeStep ? 'bg-primary' : 'bg-muted'
                )}
              />
            )}
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  'flex items-center justify-center h-8 w-8 rounded-full text-sm font-medium shrink-0',
                  index < activeStep
                    ? 'bg-primary text-primary-foreground'
                    : index === activeStep
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                )}
              >
                {index < activeStep ? (
                  <CheckCircle className="h-5 w-5" />
                ) : (
                  index + 1
                )}
              </div>
              <span
                className={cn(
                  'text-sm hidden sm:inline',
                  index <= activeStep ? 'text-foreground font-medium' : 'text-muted-foreground'
                )}
              >
                {label}
              </span>
            </div>
          </React.Fragment>
        ))}
      </div>

      {bannerError && (
        <div
          className={cn(
            'mb-4 rounded-md border p-3 text-sm flex items-start justify-between',
            bannerError.includes('Latin-1')
              ? 'border-yellow-500/50 bg-yellow-50 text-yellow-800 dark:bg-yellow-950/20 dark:text-yellow-400'
              : 'border-destructive/50 bg-destructive/10 text-destructive'
          )}
        >
          <span>{bannerError}</span>
          <button
            onClick={() => {
              setError(null);
              setDismissedErrorFor(existingStagingTable ?? null);
            }}
            className="ml-2 shrink-0 hover:opacity-70"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {renderStepContent()}
    </div>
  );
};

export default VoterStagingImport;
